<template>
  <div>
    <vx-card>
      <ValidationObserver v-slot="{ handleSubmit }" ref="form">
        <form @submit.prevent="handleSubmit">
          <!-- Policy Info -->
          <div class="vx-row flex">
            <div class="vx-col w-1/3 mt-5">
              <validation-provider
                :name="$t('sections.master.dataForm.debtRestructurePolicy.effectiveDate')"
                rules="required"
                v-slot="{ errors }"
              >
                <label class="vs-input--label">
                  {{ $t("sections.master.dataForm.debtRestructurePolicy.effectiveDate") }}*
                </label>
                <flat-pickr :config="config" class="w-full" v-model="form.effectiveDate"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </validation-provider>
            </div>

            <div class="vx-col w-2/3 mt-5">
              <validation-provider
                :name="$t('sections.master.dataForm.debtRestructurePolicy.name')"
                rules="required"
                v-slot="{ errors }"
              >
                <label class="vs-input--label">
                  {{ $t("sections.master.dataForm.debtRestructurePolicy.name") }}*
                </label>
                <vs-input type="text" v-model="form.name" class="w-full"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </validation-provider>
            </div>
          </div>

          <div class="vx-row flex">
            <div class="vx-col flex-1 mt-5">
              <validation-provider
                :name="$t('sections.master.dataForm.debtRestructurePolicy.description')"
                rules=""
                v-slot="{ errors }"
              >
                <label class="vs-input--label">
                  {{ $t("sections.master.dataForm.debtRestructurePolicy.description") }}
                </label>
                <vs-textarea v-model="form.description" rows="3"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </validation-provider>
            </div>
          </div>

          <!-- ✅ Measures -->
          <div class="vx-row mt-6">
            <div class="vx-col w-full">
              <div class="flex items-center mb-4">
                <h5 class="mb-0 mr-2">มาตรการ (Measures)</h5>
                <vs-button color="success" type="filled" size="small" icon-pack="feather" icon="icon-plus"
                           @click="addMeasure"/>
                <vs-button color="danger" type="filled" size="small" icon-pack="feather" icon="icon-minus"
                           class="ml-2"
                           @click="removeLastMeasure"
                           v-if="form.measures.length > 1"/>
              </div>

              <div v-for="(measure, mIndex) in form.measures" :key="mIndex"
                   class="mb-6 border p-4 rounded bg-gray-50">

                <!-- Measure Header -->
                <div class="vx-row">
                  <div class="vx-col w-1/6">
                    <label class="vs-input--label">{{ $t("sections.master.dataForm.measure.orderNumber") }}</label>
                    <div class="p-2 border rounded bg-gray-100 text-center">
                      {{ mIndex + 1 }}
                    </div>
                  </div>
                  <div class="vx-col flex-1">
                    <label class="vs-input--label">{{ $t("sections.master.dataForm.measure.name") }}</label>
                    <vs-input v-model="measure.name" class="w-full"/>
                  </div>
                </div>

                <div class="vx-row mt-3">
                  <div class="vx-col w-full">
                    <label class="vs-input--label">{{ $t("sections.master.dataForm.measure.description") }}</label>
                    <vs-textarea v-model="measure.description" rows="3"/>
                  </div>
                </div>

                <!-- SubMeasures -->
                <div class="mt-6 pl-4 border-l-2 border-gray-300">
                  <div class="flex items-center mb-3">
                    <h6 class="mb-0 mr-2">มาตรการย่อย (SubMeasures)</h6>
                    <vs-button color="success" type="filled" size="small" icon-pack="feather" icon="icon-plus"
                               @click="addSubMeasure(mIndex)"/>
                    <vs-button color="danger" type="filled" size="small" icon-pack="feather" icon="icon-minus"
                               class="ml-2"
                               @click="removeLastSubMeasure(mIndex)"
                               v-if="measure.subMeasures.length > 1"/>
                  </div>

                  <div v-for="(sub, sIndex) in measure.subMeasures" :key="sIndex"
                       class="mb-4 p-4 border rounded bg-white">
                    <div class="vx-row">
                      <div class="vx-col w-1/6">
                        <label class="vs-input--label">{{
                            $t("sections.master.dataForm.subMeasure.orderNumber")
                          }}</label>
                        <div class="p-2 border rounded bg-gray-100 text-center">{{ sIndex + 1 }}</div>
                      </div>
                      <div class="vx-col flex-1">
                        <label class="vs-input--label">{{ $t("sections.master.dataForm.subMeasure.name") }}</label>
                        <vs-input v-model="sub.name" class="w-full"/>
                      </div>
                    </div>
                    <div class="vx-row mt-3">
                      <div class="vx-col w-full">
                        <label class="vs-input--label">{{
                            $t("sections.master.dataForm.subMeasure.description")
                          }}</label>
                        <vs-textarea v-model="sub.description" rows="3"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Save & Reset -->
          <div class="vx-row mt-8">
            <div class="vx-col w-full flex justify-end">
              <vs-button @click="submit">{{ isEdit ? 'Update' : 'Save' }}</vs-button>
              <vs-button class="ml-4" type="border" color="warning" @click="reset">Reset</vs-button>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </vx-card>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import {ValidationProvider} from 'vee-validate'
import {createNamespacedHelpers, mapGetters} from 'vuex'
import {IStatus} from '@/constants/interface'
import {Thai} from 'flatpickr/dist/l10n/th'

const {mapActions, mapState} = createNamespacedHelpers('Master')

export default {
  components: {vSelect, ValidationProvider, flatPickr},
  data() {
    return {
      form: {
        id: null,
        name: '',
        description: '',
        effectiveDate: '',
        status: IStatus.enabled,
        measures: [
          {orderNumber: 1, name: '', description: '', subMeasures: [{orderNumber: 1, name: '', description: ''}]}
        ]
      },
      config: {
        monthSelectorType: 'static',
        allowInput: true,
        locale: Thai,
        dateFormat: 'd-m-Y'
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id
    },
    ...mapState(['status', 'debtRestructurePolicy']),
    ...mapGetters({statusesOptions: 'Master/statusesOptions'})
  },
  methods: {
    ...mapActions(['getDebtRestructurePolicy', 'createDebtRestructurePolicy', 'updateDebtRestructurePolicy']),
    addMeasure() {
      this.$set(this.form.measures, this.form.measures.length, {
        orderNumber: this.form.measures.length + 1,
        name: '',
        description: '',
        subMeasures: [{orderNumber: 1, name: '', description: ''}]
      })
    },
    removeLastMeasure() {
      if (this.form.measures.length > 1) this.form.measures.pop()
    },
    addSubMeasure(mIndex) {
      this.$set(this.form.measures[mIndex].subMeasures, this.form.measures[mIndex].subMeasures.length, {
        orderNumber: this.form.measures[mIndex].subMeasures.length + 1,
        name: '',
        description: ''
      })
    },
    removeLastSubMeasure(mIndex) {
      if (this.form.measures[mIndex].subMeasures.length > 1) {
        this.form.measures[mIndex].subMeasures.pop()
      }
    },
    async submit() {
      const vm = this
      vm.$refs['form'].validate().then(success => {
        if (!success) return

        // ดึง id ออก ไม่ให้เข้า payload
        const {id, ...rest} = vm.form

        const payload = {
          ...rest,
          effectiveDate: vm.$options.filters.convDDMMYYYYToYYYYMMDD(vm.form.effectiveDate)
        }

        if (vm.isEdit) {
          vm.updateDebtRestructurePolicy({
            id: id, // ส่ง id แยก
            form: payload,
            vm,
            redirect: '/master/debt-restructure-policy'
          })
        } else {
          vm.createDebtRestructurePolicy({
            form: payload, // ไม่มี id แล้ว
            vm,
            redirect: '/master/debt-restructure-policy'
          })
        }
      })
    },
    reset() {
      const vm = this
      if (vm.isEdit && vm.debtRestructurePolicy) {
        const p = vm.debtRestructurePolicy
        vm.form.id = p.id || null
        vm.form.effectiveDate = p.effectiveDate ? vm.$options.filters.convYYYYMMDDToDDMMYYYY(p.effectiveDate) : ''
        vm.form.name = p.name || ''
        vm.form.description = p.description || ''
        vm.form.status = p.status || IStatus.enabled
        this.$set(vm.form, 'measures',
          (p.measures && p.measures.length > 0)
            ? p.measures.map((m, mIndex) => ({
              orderNumber: mIndex + 1,
              name: m.name || '',
              description: m.description || '',
              subMeasures: (m.subMeasures && m.subMeasures.length > 0)
                ? m.subMeasures.map((s, sIndex) => ({
                  orderNumber: sIndex + 1,
                  name: s.name || '',
                  description: s.description || ''
                }))
                : [{orderNumber: 1, name: '', description: ''}]
            }))
            : [{orderNumber: 1, name: '', description: '', subMeasures: [{orderNumber: 1, name: '', description: ''}]}]
        )
      } else {
        vm.form.id = null
        vm.form.name = ''
        vm.form.description = ''
        vm.form.effectiveDate = ''
        vm.form.status = IStatus.enabled
        this.$set(vm.form, 'measures', [
          {orderNumber: 1, name: '', description: '', subMeasures: [{orderNumber: 1, name: '', description: ''}]}
        ])
      }
    }
  },
  async created() {
    const vm = this
    if (vm.isEdit) {
      await vm.getDebtRestructurePolicy({form: {id: vm.$route.params.id}})
      vm.pms('MasterDebtRestructurePolicy', 'edit', 'r')
      vm.reset()
    } else {
      vm.pms('MasterDebtRestructurePolicy', 'create', 'r')
      vm.reset()
    }
  },
  watch: {
    status: {
      handler(res) {
        if (res.processing !== null) res.processing ? this.$vs.loading() : this.$vs.loading.close()
        if (res.success) this.$vs.notify({...res.success})
        else if (res.error) this.$vs.notify({...res.error})
      },
      deep: true
    }
  }
}
</script>
